import { useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useAccountVesselScope } from '../../hooks/useAccountVesselScope';
import { useModuleEntitlements } from '../../modules/admin/module-management';
import { useEFormAppPages, useEFormSidebarReports, usePublishedEForms } from '../../modules/e-forms/useData';
import {
  getSidebarItems,
  toPublishedEFormSidebarInput,
  withPublishedEFormsInSidebar,
  withEFormAppPagesInSidebar,
  withSavedEFormReportsInSidebar,
} from './config/SidebarItems';
import type { MenuItem } from './Sidebar';

/** Permission-filtered office sidebar including published e-forms and placed reports. */
export function useOfficeSidebarItems(canView?: (moduleKey: string) => boolean): MenuItem[] {
  const { user } = useAuth();
  const vesselAccountId = useAccountVesselScope();
  const { entitlements } = useModuleEntitlements();
  const canViewEFormSubmissions =
    !canView || canView('e_form_submissions') || user?.role === 'ADMIN';
  const publishedEForms = usePublishedEForms(undefined, 1, 200, undefined, canViewEFormSubmissions);
  const sidebarReports = useEFormSidebarReports(canViewEFormSubmissions);
  const appPages = useEFormAppPages(undefined, canViewEFormSubmissions);
  return useMemo(
    () =>
      withEFormAppPagesInSidebar(
        withSavedEFormReportsInSidebar(
          withPublishedEFormsInSidebar(
            getSidebarItems(canView, vesselAccountId, entitlements, user?.typeLocation),
            toPublishedEFormSidebarInput(publishedEForms.data ?? []),
          ),
          sidebarReports.data ?? [],
        ),
        appPages.data ?? [],
      ),
    [
      canView,
      vesselAccountId,
      entitlements,
      user?.typeLocation,
      publishedEForms.data,
      sidebarReports.data,
      appPages.data,
    ],
  );
}
